import React, { useEffect, useMemo, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { BrowserRouter } from "react-router-dom";
import styled from "styled-components/macro";
import { datadogRum } from "@datadog/browser-rum";
import { LDProvider, LDContext } from "launchdarkly-react-client-sdk";
import AppContainer from "./AppContainer";
import "./App.css";
import {
  LOGIN_PAGE_URL,
  VERIFY_EMAIL_CONFIRMED_URL,
  VERIFY_EMAIL_SEND_AGAIN_URL,
} from "./utils/urlConstants";
import Loading from "./components/shared/Loading/Loading";
import { RefreshPatientInfo } from "./components/CreateAccount/Services/CreateAccountService";
import {
  billPreviewsState,
  billPreviewsLoadingState,
  patientState,
} from "./recoil/atoms";
import { getCookieAccessToken } from "./utils/requestUtils";
import { SetAnalyticsUser } from "./services/Analytics";
import {
  ANONYMOUS_LD_CONTEXT_KEY,
  LAUNCHDARKLY_CLIENT_ID,
} from "./utils/launchDarklyUtils";
import { buildPatientLdContext } from "./utils/contextUtils";
import { LdContextSync } from "./LdContextSync";
import { fetchAndUpdateBillPreviews } from "./utils/hooks/useBillPreviewsRefresh";

const Container = styled.div`
  position: relative;
  min-height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 100%;
  background: ${({ theme }) => theme.neutral50};

  &:has(.phlebotomy-mobile-finder-viewport) {
    background: ${({ theme }) => theme.white};
    height: 100vh;
    min-height: 0;
    overflow: hidden;

    @supports (height: 100dvh) {
      height: 100dvh;
    }
  }
`;

export const App = (): React.ReactElement => {
  const isLocalEnv = import.meta.env.VITE_ENV === "local";
  const [isLoading, setIsLoading] = useState(true);
  const [patientInfo, setPatientInfo] = useRecoilState(patientState);
  const setBillPreviews = useSetRecoilState(billPreviewsState);
  const setBillPreviewsLoading = useSetRecoilState(billPreviewsLoadingState);
  // Derive the LD context synchronously from patientInfo (no state/effect lag).
  // On a cold start with a session cookie, patientInfo is already loaded by the
  // time the provider mounts, so the context is the patient from the first render
  // and anonymous only when truly logged out. This avoids a transient anonymous
  // identify racing the patient one and leaving the client without lims_id.
  const ldContext = useMemo<LDContext>(
    () =>
      patientInfo
        ? buildPatientLdContext(patientInfo)
        : { kind: "user", key: ANONYMOUS_LD_CONTEXT_KEY, anonymous: true },
    [patientInfo],
  );
  const { pathname } = window.location;

  useEffect(() => {
    if (patientInfo) {
      datadogRum.setUser({ id: patientInfo.auth0.user_id });

      // Mixpanel/Analytics identification
      SetAnalyticsUser(patientInfo);
    }
  }, [patientInfo]);

  useEffect(() => {
    const abortController = new AbortController();

    if (patientInfo?.auth0?.access_token) {
      fetchAndUpdateBillPreviews(
        patientInfo.auth0.access_token,
        setBillPreviews,
        setBillPreviewsLoading,
        abortController.signal,
      );
    }

    // Cleanup: abort any in-flight request when dependencies change
    return () => {
      abortController.abort();
    };
  }, [
    patientInfo?.auth0?.access_token,
    setBillPreviews,
    setBillPreviewsLoading,
  ]);

  useEffect(() => {
    if (
      !pathname.startsWith(VERIFY_EMAIL_CONFIRMED_URL) &&
      !pathname.startsWith(VERIFY_EMAIL_SEND_AGAIN_URL) &&
      !pathname.startsWith(LOGIN_PAGE_URL)
    ) {
      const authToken = getCookieAccessToken();
      (async () => {
        if (authToken) {
          setIsLoading(true);
          await RefreshPatientInfo(authToken, setPatientInfo);
        }
        setIsLoading(false);
      })();
    } else {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <LDProvider
      clientSideID={LAUNCHDARKLY_CLIENT_ID}
      context={ldContext}
      deferInitialization
      timeout={5}
      options={{ bootstrap: "localStorage", sendEvents: !isLocalEnv }}
    >
      <LdContextSync context={ldContext} />
      <Container className="App">
        <BrowserRouter>
          <AppContainer />
        </BrowserRouter>
      </Container>
    </LDProvider>
  );
};
